// theme.js
import { createTheme } from "@mui/material/styles";


export const tintColorLight = "#023020";
export const tintColorDark = "#023020";

const getTheme = (mode) => ({
  palette: {
    mode,
    primary: {
      main: "#127351",
      light: "#2aa880",
      dark: "#0b5a3e",
      contrastText: "#fff",
    },
    secondary: {
      main: tintColorDark,
      light: "#1a5e4b",
      dark: "#011a10",
      contrastText: "#fff",
    },
    info: {
      main: "#3b82f6",
      light: "#60a5fa",
      dark: "#2563eb",
    },
    success: {
      main: "#22c55e",
      light: "#4ade80",
      dark: "#16a34a",
    },
    warning: {
      main: "#f59e0b",
      light: "#fbbf24",
      dark: "#d97706",
    },
    error: {
      main: "#ef4444",
      light: "#f87171",
      dark: "#dc2626",
    },
    grey: {
      50: "#f8fafc",
      100: "#f1f5f9",
      200: "#e2e8f0",
      300: "#cbd5e1",
      400: "#94a3b8",
      500: "#64748b",
      600: "#475569",
      700: "#334155",
      800: "#1e293b",
      900: "#0f172a",
    },
    action: {
      // Improve contrast for disabled buttons in dark mode if needed
      disabledBackground: mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
      disabled: mode === "dark" ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.26)",
    },
    background: {
      default: mode === "dark" ? "#0F1117" : "#ffffff",
      paper: mode === "dark" ? "#13151D" : "#ffffff",
    },
    text: {
      primary: mode === "dark" ? "#E8E8ED" : "#1D1D1F",
      secondary: mode === "dark" ? "#8E8E93" : "#6E6E73",
    },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0 1px 2px rgba(0,0,0,0.05)",
    "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
    "0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.04)",
    "0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -4px rgba(0,0,0,0.04)",
    "0 20px 25px -5px rgba(0,0,0,0.08), 0 8px 10px -6px rgba(0,0,0,0.04)",
    "0 25px 50px -12px rgba(0,0,0,0.15)",
    "0 25px 50px -12px rgba(0,0,0,0.20)",
    "0 25px 50px -12px rgba(0,0,0,0.25)",
    "0 30px 60px -12px rgba(0,0,0,0.28)",
    "0 35px 60px -15px rgba(0,0,0,0.30)",
    // fill remaining MUI shadow slots with a repeat of slot 10
    "0 35px 60px -15px rgba(0,0,0,0.30)",
    "0 35px 60px -15px rgba(0,0,0,0.30)",
    "0 35px 60px -15px rgba(0,0,0,0.30)",
    "0 35px 60px -15px rgba(0,0,0,0.30)",
    "0 35px 60px -15px rgba(0,0,0,0.30)",
    "0 35px 60px -15px rgba(0,0,0,0.30)",
    "0 35px 60px -15px rgba(0,0,0,0.30)",
    "0 35px 60px -15px rgba(0,0,0,0.30)",
    "0 35px 60px -15px rgba(0,0,0,0.30)",
    "0 35px 60px -15px rgba(0,0,0,0.30)",
    "0 35px 60px -15px rgba(0,0,0,0.30)",
    "0 35px 60px -15px rgba(0,0,0,0.30)",
    "0 35px 60px -15px rgba(0,0,0,0.30)",
    "0 35px 60px -15px rgba(0,0,0,0.30)",
  ],
  typography: {
    fontFamily:
      '"Bricolage Grotesque", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 800,
    h1: {
      fontSize: "clamp(2.5rem, 5vw, 3.5rem)",
      fontWeight: 800,
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontSize: "clamp(2rem, 4vw, 2.75rem)",
      fontWeight: 700,
      lineHeight: 1.15,
      letterSpacing: "-0.015em",
    },
    h3: {
      fontSize: "clamp(1.5rem, 3vw, 2rem)",
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: "-0.01em",
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
      lineHeight: 1.25,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h6: {
      fontSize: "1.1rem",
      fontWeight: 600,
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: 18,
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: 16,
      color: mode === "dark" ? "#8E8E93" : "#475569",
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontWeight: 400,
      fontSize: 16,
      lineHeight: 1.65,
    },
    body2: {
      fontSize: 14,
      lineHeight: 1.6,
    },
    button: {
      fontWeight: 600,
      letterSpacing: "0.01em",
      textTransform: "none",
    },
    caption: {
      fontSize: 12,
      color: mode === "dark" ? "#636366" : "#64748b",
      lineHeight: 1.5,
    },
    overline: {
      fontSize: 11,
      letterSpacing: "0.08em",
      fontWeight: 700,
      textTransform: "uppercase",
    },
  },
  components: {
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          transition:
            "background-color 0.3s ease, box-shadow 0.3s ease, backdrop-filter 0.3s ease",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 999,
          textTransform: "none",
          fontWeight: 600,
          padding: "10px 24px",
          transition: "all 0.25s cubic-bezier(0.22, 1, 0.36, 1)",
          "&:hover": {
            transform: "translateY(-1px)",
          },
        },
        containedPrimary: {
          background: "linear-gradient(180deg, #2aa880, #127351)",
          boxShadow: "0 4px 14px rgba(42, 168, 128, 0.25)",
          "&:hover": {
            background: "linear-gradient(180deg, #32b88e, #158a5f)",
            boxShadow: "0 6px 20px rgba(42, 168, 128, 0.35)",
            transform: "translateY(-1px)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        rounded: {
          borderRadius: 16,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid #e2e8f0",
          boxShadow: mode === "dark" ? "0 2px 12px rgba(0,0,0,0.4)" : "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
          transition:
            "box-shadow 0.3s ease, transform 0.3s ease, border-color 0.3s ease",
          "&:hover": {
            boxShadow:
              mode === "dark" ? "0 10px 25px rgba(0,0,0,0.4)" : "0 10px 25px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)",
            transform: "translateY(-2px)",
          },
        },
      },
    },
    MuiSkeleton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
        },
        wave: {
          "&::after": {
            background:
              "linear-gradient(90deg, transparent, rgba(42, 168, 128, 0.06), transparent)",
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          height: 3,
        },
        bar: {
          borderRadius: 999,
          background: "linear-gradient(90deg, #2aa880, #127351)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: 999,
        },
      },
    },
  },
});

export default getTheme;
